import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from '@/utils/i18n';
import { gql } from '@apollo/client';
import {
  LeadGenerator_ProspectFinderCategoryOptionsQuery,
  LeadGenerator_ProspectFinderCategoryOptionsQueryVariables,
} from '@/types';
import { partnerSupplier } from '@/utils/environment';
import { SortOrder } from '@/hooks/use-nearby-businesses';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

enum BusinessStatus {
  CURRENT = 'CURRENT',
  PROSPECT = 'PROSPECT',
}

interface DefaultCategories {
  enabled: boolean;
  selectedCategories: Set<string>;
}

const CATEGORIES_KEY = 'salesRepLevelCategories';

export const sortOptions = [
  { label: i18n.t('best_match'), value: SortOrder.BEST_MATCH },
  { label: i18n.t('distance'), value: SortOrder.DISTANCE },
  { label: i18n.t('rating'), value: SortOrder.RATING },
];
export const priceOptions = [
  { label: i18n.t('all'), value: '' },
  { label: '$', value: '1' },
  { label: '$$', value: '2' },
  { label: '$$$', value: '3' },
  { label: '$$$$', value: '4' },
];

export const GET_PROSPECT_FINDER_CATEGORY_OPTIONS = gql`
  query LeadGenerator_ProspectFinderCategoryOptions(
    $businessOrganizationId: uuid!
  ) {
    getProspectFinderCategoryOptions(
      business_organization_id: $businessOrganizationId
    ) {
      display_name
      yelp_alias
    }
  }
`;

interface FilterType {
  businessStatus: BusinessStatus | null;
  defaultCategories: DefaultCategories;
  isDefaultLoaded: boolean;
  priceRange: string;
  selectedCategories: Set<string>;
  showCategories: boolean;
  showPriceRange: boolean;
  showSortOrder: boolean;
  sortOrder: SortOrder;
}

export const useLeadGeneratorFiltering = (
  onBeforeFilterPillUpdate: () => void,
) => {
  const [filters, setFilters] = useState<FilterType>({
    businessStatus: null,
    defaultCategories: {
      enabled: false,
      selectedCategories: new Set<string>(),
    },
    isDefaultLoaded: false,
    priceRange: '',
    selectedCategories: new Set<string>(),
    showCategories: false,
    showPriceRange: false,
    showSortOrder: false,
    sortOrder: SortOrder.BEST_MATCH,
  });

  const {
    businessStatus,
    defaultCategories,
    isDefaultLoaded,
    priceRange,
    selectedCategories,
    sortOrder,
  } = filters;

  const {
    data: categoryData,
    error: categoryOptionsError,
    loading: categoryOptionsLoading,
  } = useWrappedQuery<
    LeadGenerator_ProspectFinderCategoryOptionsQuery,
    LeadGenerator_ProspectFinderCategoryOptionsQueryVariables
  >(GET_PROSPECT_FINDER_CATEGORY_OPTIONS, {
    variables: {
      businessOrganizationId: partnerSupplier.businessOrganizationUUID,
    },
  });

  const categoryOptions = useMemo(() => {
    if (!categoryData?.getProspectFinderCategoryOptions) {
      return [];
    }
    return categoryData.getProspectFinderCategoryOptions.map((option) => ({
      label: option.display_name,
      value: option.yelp_alias,
    }));
  }, [categoryData]);

  const categoryOptionsIndex = useMemo(
    () => new Set(categoryOptions.map((option) => option.value)),
    [categoryOptions],
  );

  const updateFilters = useCallback((newFilters: Partial<FilterType>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Restore the user's saved filter selections from AsyncStorage, validating against current options
  useEffect(() => {
    if (categoryOptionsLoading || categoryOptionsError) {
      return;
    }

    (async () => {
      try {
        const categoriesJSON = await AsyncStorage.getItem(CATEGORIES_KEY);

        if (categoriesJSON) {
          const parsedCategories = JSON.parse(categoriesJSON);

          updateFilters({
            defaultCategories: {
              enabled: parsedCategories.enabled,
              selectedCategories: new Set(
                parsedCategories.selectedCategories.filter((v: string) =>
                  categoryOptionsIndex.has(v),
                ),
              ),
            },
          });
        }
      } catch (e) {
        // set generic default
        updateFilters({
          defaultCategories: {
            enabled: false,
            selectedCategories: new Set<string>(),
          },
        });
      } finally {
        updateFilters({ isDefaultLoaded: true });
      }
    })();
  }, [
    categoryOptionsLoading,
    categoryOptionsError,
    categoryOptionsIndex,
    updateFilters,
  ]);

  useEffect(() => {
    (async () => {
      /* Update AsyncStorage whenever defaultCuisines change
       * and default settings have been loaded
       */
      if (defaultCategories && isDefaultLoaded) {
        await AsyncStorage.setItem(
          CATEGORIES_KEY,
          JSON.stringify({
            ...defaultCategories,
            selectedCategories: Array.from(
              defaultCategories.selectedCategories,
            ),
          }),
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCategories]);

  // filter pill props
  const buttonProps = useMemo(() => {
    return [
      {
        active: businessStatus === BusinessStatus.CURRENT,
        collapsed:
          businessStatus !== BusinessStatus.CURRENT && businessStatus !== null,
        label: i18n.t('current'),
        onClear: () => {
          onBeforeFilterPillUpdate();
          updateFilters({ businessStatus: null });
        },
        onPress: () => {
          onBeforeFilterPillUpdate();
          updateFilters({
            businessStatus:
              businessStatus === BusinessStatus.CURRENT
                ? null
                : BusinessStatus.CURRENT,
          });
        },
      },
      {
        active: businessStatus === BusinessStatus.PROSPECT,
        collapsed:
          businessStatus !== BusinessStatus.PROSPECT && businessStatus !== null,
        label: i18n.t('prospects'),
        onClear: () => {
          onBeforeFilterPillUpdate();
          updateFilters({ businessStatus: null });
        },
        onPress: () => {
          onBeforeFilterPillUpdate();
          updateFilters({
            businessStatus:
              businessStatus === BusinessStatus.PROSPECT
                ? null
                : BusinessStatus.PROSPECT,
          });
        },
      },
      {
        active: defaultCategories.enabled
          ? defaultCategories.selectedCategories.size > 0
          : selectedCategories.size > 0,
        count: defaultCategories.enabled
          ? defaultCategories.selectedCategories.size
          : selectedCategories.size,
        dropDown: true,
        label: i18n.t('categories'),
        onClear: () => {
          if (defaultCategories.enabled) {
            updateFilters({
              defaultCategories: {
                ...defaultCategories,
                selectedCategories: new Set(),
              },
            });
          } else {
            updateFilters({ selectedCategories: new Set() });
          }
        },
        onPress: () => updateFilters({ showCategories: true }),
      },
      {
        active: sortOrder !== SortOrder.BEST_MATCH,
        dropDown: true,
        label:
          sortOrder !== SortOrder.BEST_MATCH
            ? i18n.t('sort') + ': ' + i18n.t(sortOrder)
            : i18n.t('sort'),
        onClear: () => updateFilters({ sortOrder: SortOrder.BEST_MATCH }),
        onPress: () => updateFilters({ showSortOrder: true }),
      },
      {
        active: priceRange !== '',
        dropDown: true,
        label: '$-$$$$',
        onClear: () => updateFilters({ priceRange: '' }),
        onPress: () => updateFilters({ showPriceRange: true }),
      },
    ];
  }, [
    businessStatus,
    defaultCategories,
    priceRange,
    selectedCategories,
    sortOrder,
    updateFilters,
    onBeforeFilterPillUpdate,
  ]);

  const categories = useMemo(() => {
    // default stored categories
    if (defaultCategories.enabled) {
      if (defaultCategories.selectedCategories.size) {
        return Array.from(defaultCategories.selectedCategories)
          .join(',')
          .toLowerCase();
      } else {
        return '';
      }
    } else {
      //  user selected
      if (selectedCategories.size) {
        return Array.from(selectedCategories).join(',').toLowerCase();
      } else {
        return '';
      }
    }
  }, [selectedCategories, defaultCategories]);

  const isCurrentRestaurant = useMemo(() => {
    return businessStatus === BusinessStatus.CURRENT
      ? true
      : businessStatus === BusinessStatus.PROSPECT
        ? false
        : undefined;
  }, [businessStatus]);

  return useMemo(
    () => ({
      buttonProps,
      categories,
      categoryOptions,
      filters,
      isCurrentRestaurant,
      updateFilters,
    }),
    [
      buttonProps,
      categories,
      categoryOptions,
      filters,
      isCurrentRestaurant,
      updateFilters,
    ],
  );
};
